import React, { useMemo, useState } from 'react';

import { CustomAccordion as Accordion } from '@planne-software/uni/Accordion';
import { ScopedCssBaseline } from '@planne-software/uni/mui/material';

import { useAppDispatch, useWidgetsTheme } from '@src/core/hooks';
import useTranslate from '@src/i18n/useTranslate';

import {
  AccordionContent,
  AccordionWrapper,
  CartProduct,
  Container,
  InformationLabel,
  InformationValue,
  ProductImage,
  ProductInformation,
  ProductInformationContainer,
  ProductName,
  TariffContainer,
  TariffList,
} from './styles';
import { OutlinedButton } from '@src/components/reusable';
import ErrorWarning from '../ErrorWarning';
import CartItemContainer from '.';
import TotalPriceSummary from './TotalPriceSummary';
import { removeCartPackage } from '@src/features/cart';
import { calculatePackageTotalPriceSummary, dateBySellingMode, formatPackageTariffs } from '@src/common/cart';
import { CartItem, ProductPackage } from '@src/core/buyerApiClient';

interface CartPackageItemContainerProps {
  packageItem: ProductPackage;
  items: CartItem[];
  editable?: boolean;
  showErrors?: boolean;
}

const CartPackageItemContainer = (props: Readonly<CartPackageItemContainerProps>) => {
  const { packageItem, items, editable = true, showErrors = true } = props;

  const [removeItemError, setRemoveItemError] = useState(false);
  const [disabledItem, setDisabledItem] = useState(false);
  const { i18n } = useTranslate();
  const dispatch = useAppDispatch();

  const removeItem = () => {
    setDisabledItem(true);
    setRemoveItemError(false);

    try {
      dispatch(removeCartPackage(items[0]!.productPackageUnitId!));
    } catch (error) {
      setRemoveItemError(true);
    } finally {
      setDisabledItem(false);
    }
  };

  const mainColor = useWidgetsTheme('actionColor');

  const selectedStringDates = useMemo(
    () =>
      items
        .map((item) =>
          dateBySellingMode(
            item.scheduleDate,
            item.formattedScheduleTimeMinutes,
            i18n('account_sale_item_without_date'),
          ),
        )
        .join(', '),
    [items, i18n],
  );

  const selectedTariffsMap = useMemo(() => formatPackageTariffs(items), [items]);

  const { total, discount, previousPrice } = useMemo(() => calculatePackageTotalPriceSummary(items), [items]);

  const hasPackageItemErrors = useMemo(() => {
    return items.some((item) => item.errors && item.errors.length > 0);
  }, [items]);

  return (
    <Container>
      <CartProduct>
        <ProductImage hidden={!packageItem.image?.styles[0]?.url}>
          <img src={packageItem.image?.styles[0]?.url ?? ''}></img>
        </ProductImage>

        <ProductInformationContainer>
          <ProductName>{packageItem.name}</ProductName>

          <ProductInformation>
            <InformationLabel>{i18n('cart_selectedDate')}:</InformationLabel>

            <InformationValue>
              <span>{selectedStringDates}</span>
            </InformationValue>
          </ProductInformation>

          <TariffContainer>
            <InformationLabel>{i18n('cart_selectedTariff')}:</InformationLabel>

            <TariffList>
              {Object.entries(selectedTariffsMap).map(([name, quantity]) => (
                <InformationValue key={name}>
                  {name} - {quantity}
                </InformationValue>
              ))}
            </TariffList>
          </TariffContainer>
        </ProductInformationContainer>
      </CartProduct>

      <ScopedCssBaseline>
        <AccordionWrapper>
          <Accordion
            title={i18n('cart_package_details')}
            content={
              <AccordionContent aria-label='accordion-content'>
                {items.map((item) => (
                  <CartItemContainer key={item.id} item={item} editable={false} showErrors={showErrors} />
                ))}
              </AccordionContent>
            }
          />
        </AccordionWrapper>
      </ScopedCssBaseline>

      <TotalPriceSummary total={total} discount={discount} previousPrice={previousPrice} />

      {hasPackageItemErrors && showErrors ? <ErrorWarning>{i18n('cart_package_error')}</ErrorWarning> : null}

      {editable ? (
        <OutlinedButton onClick={removeItem} color={mainColor} disabled={disabledItem} showLoadingOnDisabled>
          {i18n('cart_cancel')}
        </OutlinedButton>
      ) : null}

      {removeItemError ? <ErrorWarning>{i18n('cart_item_remove_error')}</ErrorWarning> : null}
    </Container>
  );
};

export default CartPackageItemContainer;
